import React, { useRef, useMemo } from "react";
import styled from "styled-components";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import img1 from "../../assets/trustedBy/tb1.jpeg";
import img2 from "../../assets/trustedBy/tb2.jpeg";
import img3 from "../../assets/trustedBy/tb3.jpeg";
import img4 from "../../assets/trustedBy/tb4.jpeg";
import img5 from "../../assets/trustedBy/tb5.jpeg";
import img6 from "../../assets/trustedBy/tb6.jpeg";
import img7 from "../../assets/trustedBy/tb7.jpeg";
import img8 from "../../assets/trustedBy/tb8.jpeg";
// img9 is removed to keep the math clean for 4x2 or 3x3 grids

gsap.registerPlugin(ScrollTrigger);

const CLIENT_IMAGES = [img1, img2, img3, img4, img5, img6, img7, img8];

const NAV_HEIGHT = "80px";

const StructuredGrid = () => {
  const container = useRef();

  const tilts = useMemo(() => {
    return CLIENT_IMAGES.map(() => Math.random() * 6 - 3);
  }, []);

  useGSAP(
    () => {
      const photos = gsap.utils.toArray(".photo-grid-item");

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container.current,
          start: "top top",
          end: "+=200%",
          scrub: 1,
          pin: true,
        },
      });

      photos.forEach((photo, i) => {
        tl.fromTo(
          photo,
          { scale: 0.5, opacity: 0, filter: "blur(15px)", y: 50 },
          {
            scale: 1,
            opacity: 1,
            filter: "blur(0px)",
            y: 0,
            duration: 1,
            ease: "power2.out",
            rotate: tilts[i],
          },
          i * 0.1,
        );
      });
    },
    { scope: container },
  );

  return (
    <SectionWrapper ref={container}>
      <BackgroundTitle>zufriedene Kunden</BackgroundTitle>
      <SafeZone>
        <GridContainer>
          {CLIENT_IMAGES.map((img, i) => (
            <GridItemWrapper key={i} className="photo-grid-item">
              <TiltedInner $tilt={tilts[i]}>
                <img src={img} alt={`Client ${i}`} />
              </TiltedInner>
            </GridItemWrapper>
          ))}
        </GridContainer>
      </SafeZone>
    </SectionWrapper>
  );
};

export default StructuredGrid;

const SectionWrapper = styled.section`
  height: 100vh;
  width: 100vw;
  position: relative;
  overflow: hidden;
  background-color: #f9f8f6; /* Matching your academy theme */
`;

const SafeZone = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
`;

const GridContainer = styled.div`
  display: grid;
  gap: 1.5rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  z-index: 2;

  /* BIG DEVICES: 4x2 */
  grid-template-columns: repeat(4, 1fr);

  @media (max-width: 1024px) {
    /* TABLET: 3x3 (Note: you'd need 9 images for a full 3x3) */
    grid-template-columns: repeat(3, 1fr);
    max-width: 90%;
    gap: 1rem;
  }

  @media (max-width: 600px) {
    /* MOBILE: 2x4 */
    grid-template-columns: repeat(2, 1fr);
    gap: 0.8rem;
  }
`;

const GridItemWrapper = styled.div`
  position: relative;
  width: 100%;
  /* Using padding-bottom hack or aspect-ratio to prevent overflow */
  aspect-ratio: 4 / 5;
  transform-style: preserve-3d;
`;

const TiltedInner = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: white;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.5s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const BackgroundTitle = styled.h2`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: "The Seasons", serif;
  font-weight: 100;
  font-size: 10vw;
  color: rgba(90, 82, 72, 0.08); /* Subtler version of your main color */
  z-index: 0;
  pointer-events: none;
  white-space: nowrap;
  text-transform: uppercase;
`;
